<template>
  <div stye="width: 100%;">
    <div class="screener-card-size screener-card-border">
      <!-- Top Section -->
      <div class="screener-card-top">
        <div v-if="isBuyCard" class="pvd-title-root duration-apy-heading white-background bottom-border-separator">
          <p class="pvd-title__heading pvd-title__heading--level-3 duration-width" data-cy="card-title-duration">
            {{ duration }} CD
          </p>
          <p class="pvd-title__heading pvd-title__heading--level-3 regular-font" data-cy="card-title-apy">
            {{ fillInApy(clientConstants.APY_TITLE) }}
          </p>
        </div>
        <div v-if="isUnavailableCard" class="light-gray-background bottom-border-background"></div>
      </div>

      <!-- Middle Section -->
      <div class="screener-card-middle">
        <div v-if="isBuyCard" class="centered-table white-background">
          <div class="centered-table-cell">
            <p class="card-title" data-cy="card-title">{{ title }}</p>
            <p class="card-return-title" data-cy="card-return-title">
              {{ fillInDuration(clientConstants.CALCULATED_RETURN) }}
            </p>
            <p class="green-amount" data-cy="card-return-amount">
              {{ returnAmount }}<sup v-if="!showFractionalIcon">*</sup>
            </p>
          </div>
        </div>
        <div
          v-if="isUnavailableCard"
          class="centered-table light-gray-background educational-text"
          :id="isUnavailableCardTextId">
          <div class="centered-table-cell">
            <p>{{ title }}</p>
          </div>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="screener-card-bottom">
        <div v-if="isBuyCard" class="centered-table white-background">
          <div class="centered-table-cell">
            <div class="half-height">
              <pvd-button
                :data-cy="buttonCyId"
                :id="buyButtonId"
                @click="trackChoice()"
                :pvd-href="searchURL"
                pvd-native-anchor-attributes='{"target": "_blank"}'
                pvd-icon-right="action__open-link">
                {{ clientConstants.REVIEW_AND_BUY }}
              </pvd-button>
            </div>
            <div class="half-height">
              <img
                v-if="showFractionalIcon"
                :id="iconId"
                class="fractional-image"
                src="/prgw/digital/finewissues/client/assets/fractional-cd.svg"
                alt="Circle with fractional slice pulled out" />
            </div>
          </div>
        </div>
        <div v-if="isUnavailableCard" class="centered-table light-gray-background">
          <pvd-link
            :pvd-id="isUnavailableCardLinkId"
            data-cy="empty-card-link"
            :pvd-href="searchURL"
            pvd-new-window
            pvd-disable-underline>
            {{ clientConstants.ALL_CD_OFFERINGS }}
          </pvd-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { ClientConstants, ScreenerCardStyle } from './../client.constants';
import { Utils } from './../utils/Utils';

export default defineComponent({
  name: 'ScreenerCard',
  components: {},

  props: {
    idPrefix: { type: String, required: true },
    cardStyle: { type: Number, required: true },
    cusip: { type: String, required: false, default: '' },
    duration: { type: String, required: false, default: '' },
    apy: { type: String, required: false, default: '' },
    returnAmount: { type: String, required: false, default: 'N/A' },
    title: { type: String, required: false, default: '' },
    showFractionalIcon: { type: Boolean, required: false, default: false },
    searchURL: { type: String, required: false, default: '' }
  },

  mounted() {
    window.addEventListener('resize', this.onResize);
  },
  unmounted() {
    window.removeEventListener('resize', this.onResize);
  },
  computed: {},

  setup(props) {
    const isBuyCard = computed(() => props.cardStyle == ScreenerCardStyle.BUY_CARD);
    const isUnavailableCard = computed(() => props.cardStyle == ScreenerCardStyle.UNAVAILABLE_CARD);
    const isUnavailableCardTextId = computed(() => props.idPrefix + '-empty-card-text');
    const isUnavailableCardLinkId = computed(() => props.idPrefix + '-empty-card-link');
    const buyButtonId = computed(() => props.idPrefix + '-buy-button');
    const buttonCyId = computed(() => props.idPrefix + '-cy-button');
    const iconId = computed(() => props.idPrefix + '-card-icon');

    return {
      isBuyCard,
      isUnavailableCard,
      isUnavailableCardTextId,
      isUnavailableCardLinkId,
      buyButtonId,
      buttonCyId,
      iconId
    };
  },

  data: () => {
    return {
      clientConstants: ClientConstants,
      windowWidth: window.innerWidth
    };
  },
  methods: {
    onResize: function () {
      this.windowWidth = window.innerWidth;
    },
    fillInDuration: function (text: string): string {
      return text.replace('{{ duration }}', this.duration ?? '');
    },
    fillInApy: function (text: string): string {
      return text.replace('{{ apy }}', this.apy ?? '');
    },
    trackChoice() {
      try {
        switch (this.idPrefix) {
          case 'top':
            Utils.trackingFunction(this.clientConstants.SEARCH_PAGE, this.clientConstants.TOP_CARD_INTERACTION);
            break;
          case 'whole':
            Utils.trackingFunction(this.clientConstants.SEARCH_PAGE, this.clientConstants.WHOLE_CARD_INTERACTION);
            break;
          case 'fractional':
            Utils.trackingFunction(this.clientConstants.SEARCH_PAGE, this.clientConstants.FRACTIONAL_CARD_INTERACTION);
            break;
          default:
            break;
        }
      } catch (err) {
        console.log('Error In track analytics ' + err);
      }
    }
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import '@fmr-pr101742/providence-visual-style/styles/visual-style.scss';

// These colors match Figma but are not defined in Providence
$very-light-gray: #f1f1f1;
$currency-green: #368727;

$border-thickness: 0.05rem;
$border-radius: 0.5rem;

$box-size-desktop-height: 16rem;
$top-desktop-height: 0.25 * $box-size-desktop-height - 2 * $border-thickness;
$middle-desktop-height: 0.5 * $box-size-desktop-height;
$bottom-desktop-height: 0.25 * $box-size-desktop-height - $border-thickness;

div.bottom-border-separator {
  border-bottom: $border-thickness solid $pvd-border-color-table-row;
}
div.bottom-border-background {
  border-bottom: $border-thickness solid $very-light-gray;
}
.regular-font {
  font-weight: 400 !important;
}
p.spacer {
  margin-bottom: 0.625rem;
}
.screener-card-border {
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  border: $border-thickness solid $pvd-border-color-table-row;
  border-radius: $border-radius;
  padding: 0;
  margin: 0.3125rem;
}
.screener-card-size {
  padding: 0;
  width: 100%;
  max-width: 100%;
  height: $box-size-desktop-height;
}
.screener-card-top {
  width: 100%;
  height: $top-desktop-height;
}
.screener-card-middle {
  width: 100%;
  height: $middle-desktop-height;
}
.screener-card-bottom {
  width: 100%;
  border-bottom-left-radius: $border-radius;
  border-bottom-right-radius: $border-radius;
  height: $bottom-desktop-height;
}
.screener-card-top > div,
.screener-card-middle > div,
.screener-card-bottom > div {
  height: 100%;
  width: 100%;
}
// keep background colors from extending outside of rounded corners
.screener-card-top div {
  border-top-left-radius: $border-radius;
  border-top-right-radius: $border-radius;
}
.screener-card-bottom div {
  border-bottom-left-radius: $border-radius;
  border-bottom-right-radius: $border-radius;
}
.centered-table {
  display: table;
  text-align: center;
}
.centered-table-cell {
  display: table-cell;
  vertical-align: middle;
}
.half-height {
  height: 50%;
}
.fractional-image {
  float: right;
  padding-right: 0.5em;
}
.duration-apy-heading {
  display: inline-flex;
  align-items: center;
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  flex-direction: row;
  padding-bottom: 0;
  justify-content: space-between;
}
.duration-width {
  width: 60%;
}
.card-title {
  @include pvd-text-medium;
  font-weight: 700;
  text-align: center;
  margin: 0;
}
.card-return-title {
  @include pvd-text-medium;
  color: $pvd-color-palette-neutral-40;
  text-align: center;
  margin: 0;
}
.green-amount {
  @include pvd-heading-level-3;
  color: $currency-green;
  text-align: center;
}
.screener-card-middle div.educational-text p,
.screener-card-bottom div.educational-text p {
  @include pvd-heading-level-4;
  font-weight: 400;
  text-align: center;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}
.light-gray-background {
  background-color: $very-light-gray;
}
.white-background {
  background-color: $pvd-color-palette-white;
}
</style>
